import { addCurrentActivations, getDisposition } from "./combatant.mjs";

const MODULE = "lancer-initiative";

/**
 * Sets the active turn to the combatant passed by id or calls
 * {@link LancerCombat#requestActivation()} if the user does not have
 * permission to modify the combat
 * @param id {string}
 * @param [override]
 */
export async function activateCombatant(combat, id, override = false) {
  if (!game.user?.isGM && !override && !(combat.turn == null && combat.combatants.get(id)?.isOwner))
    return requestActivation(combat, id);
  const combatant = combat.getEmbeddedDocument("Combatant", id);
  if (!combatant?.getFlag(MODULE, "activations.value")) return combat;
  await addCurrentActivations(combatant, -1);
  const turn = combat.turns.findIndex(t => t.id === id);
  const opts = { direction: 1, worldTime: { delta: CONFIG.time.turnTime } };
  return combat.update({ turn }, opts);
}

export async function deactivateCombatant(combat, id) {
  const turn = combat.turns.findIndex(t => t.id === id);
  if (turn !== combat.turn) return combat;
  if (!combat.turns[turn].testUserPermission(game.user, "OWNER") && !game.user?.isGM) return combat;
  const opts = { direction: 0, worldTime: { delta: 0 } };
  return combat.update({ turn: null }, opts);
}

/**
 * Calls any Hooks registered for "LancerCombatRequestActivate".
 * @param id {string}
 */
async function requestActivation(combat, id) {
  Hooks.callAll("LancerCombatRequestActivate", combat, id);
  return combat;
}

/// libWrapper
/**
 * @param {string} announcement     The announcement that should be played: "startEncounter", "nextUp", or "yourTurn".
 * @this {Combat}
 */
export function playCombatSound(wrapped, announcement) {
  if (announcement === "nextUp") return;
  return wrapped(announcement);
}

export function sortCombatants(wrapped, a, b) {
  // Sort by Players then Neutrals then Hostiles
  const dc = getDisposition(b) - getDisposition(a);
  if (dc !== 0) return dc;
  return wrapped(a, b);
}

/** @this {Combat} */
export async function nextTurn() {
  const updateData = { turn: null };
  const updateOptions = { advanceTime: 0, direction: 0 };
  Hooks.callAll("combatTurn", this, updateData, updateOptions);
  return this.update(updateData, updateOptions);
}
/** @this {Combat} */
export async function previousTurn() {
  if (this.turn == null) return this;
  const updateData = { turn: null };
  const updateOptions = { advanceTime: -CONFIG.time.turnTime, direction: -1 };
  // await this.combatant?.modifyCurrentActivations(1);
  Hooks.callAll("combatTurn", this, updateData, updateOptions);
  return this.update(updateData, updateOptions);
}

export async function resetAll() {
  this.combatants.forEach(c =>
    c.updateSource({
      initiative: null,
      "flags.lancer-initiative.activations.value":
        this.started && !(this.settings.skipDefeated && c.isDefeated)
          ? c.getFlag("lancer-initiative", "activations.max")
          : 0,
    })
  );
  return this.update({ turn: null, combatants: this.combatants.toObject() }, { diff: false });
}

/// Hooks
/** @param {Combat} combat */
export function combatStart(combat, data) {
  data.turn = null;
  combat.update({
    combatants: combat.combatants.map(c => ({
      _id: c._id,
      "flags.lancer-initiative.activations.value":
        data.round > 0 && !(combat.settings.skipDefeated && c.isDefeated)
          ? c.getFlag(MODULE, "activations.max") ?? 1
          : 0,
    })),
  });
}
/** @param {Combat} combat */
export function combatRound(combat, data, opts) {
  data.turn = null;
  opts.worldTime.delta = CONFIG.time.roundTime * opts.direction;
  combat.update({
    combatants: combat.combatants.map(c => ({
      _id: c._id,
      "flags.lancer-initiative.activations.value":
        data.round > 0 && !(combat.settings.skipDefeated && c.isDefeated)
          ? c.getFlag(MODULE, "activations.max") ?? 1
          : 0,
    })),
  });
  if (opts.direction < 0) opts.worldTime.delta -= combat.turns.length * CONFIG.time.turnTime;
}
